// pages/About.js
import React from "react";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Avatar, Chip, Paper, LinearProgress, Divider,
} from "@mui/material";
import BusinessIcon    from "@mui/icons-material/Business";
import VisibilityIcon  from "@mui/icons-material/Visibility";
import PeopleIcon      from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon  from "@mui/icons-material/TrendingUp";
import HandshakeIcon   from "@mui/icons-material/Handshake";
import LightbulbIcon   from "@mui/icons-material/Lightbulb";
import FavoriteIcon    from "@mui/icons-material/Favorite";
import { useInView }   from "react-intersection-observer";
import { keyframes }   from "@mui/system";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const BLUE = "#1565C0";
const DARK = "#0D47A1";
const LIGHT = "#1976D2";

const About = () => {
  const { ref: valuesRef,  inView: valuesInView }  = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: cultureRef, inView: cultureInView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const stats = [
    { number: "20+",  label: "Projects Delivered", icon: <TrendingUpIcon /> },
    { number: "10+",  label: "Happy Clients",       icon: <HandshakeIcon /> },
    { number: "6",    label: "Team Members",        icon: <PeopleIcon /> },
    { number: "2",    label: "Industry Awards",     icon: <EmojiEventsIcon /> },
  ];

  const values = [
    { icon: <LightbulbIcon sx={{ fontSize: 50 }} />, title: "Innovation First",     description: "We embrace cutting-edge technologies to deliver solutions that give clients a competitive advantage." },
    { icon: <FavoriteIcon  sx={{ fontSize: 50 }} />, title: "Client Success",       description: "Your success is our success. We go above and beyond to ensure every project exceeds expectations." },
    { icon: <BusinessIcon  sx={{ fontSize: 50 }} />, title: "Excellence",           description: "We maintain the highest standards in code quality, client communication, and project delivery." },
    { icon: <VisibilityIcon sx={{ fontSize: 50 }} />, title: "Transparency",        description: "Honest communication, clear timelines, and transparent pricing. Trust is built through openness." },
    { icon: <PeopleIcon    sx={{ fontSize: 50 }} />, title: "Collaboration",        description: "Great solutions emerge from great teamwork. We work closely with your team as a true partner." },
    { icon: <TrendingUpIcon sx={{ fontSize: 50 }} />, title: "Continuous Growth",  description: "We invest in our team's development and stay ahead of trends to bring the best solutions to clients." },
  ];

  const culture = [
    { title: "Work-Life Balance",        description: "Flexible hours, remote work options, and generous vacation policy",           percentage: 95  },
    { title: "Learning & Development",   description: "Annual training budget, conference attendance, certification support",         percentage: 100 },
    { title: "Collaborative Environment",description: "Open communication, knowledge sharing, team building activities",             percentage: 98  },
    { title: "Competitive Benefits",     description: "Health insurance, retirement plans, performance bonuses",                     percentage: 92  },
  ];

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DARK} 0%, ${BLUE} 50%, ${LIGHT} 100%)`,
          py: 15, mt: 8, position: "relative", overflow: "hidden",
          "&::before": {
            content: '""', position: "absolute",
            top: "-30%", right: "-5%",
            width: "400px", height: "400px",
            background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", color: "white", position: "relative", zIndex: 1 }}>
            <Typography variant="overline" sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "0.1em", mb: 2, display: "block" }}>
              ABOUT QUANTUM IT AI INC
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: "2.5rem", md: "4rem" }, fontWeight: 800, mb: 3 }}>
              Accelerating Innovation for the Future
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: "800px", mx: "auto", opacity: 0.93, lineHeight: 1.8 }}>
              We are a team of passionate technologists, innovators, and problem-solvers dedicated to helping businesses succeed with the latest technology.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Stats ── */}
      <Container maxWidth="lg" sx={{ mt: -6, position: "relative", zIndex: 2, mb: 12 }}>
        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  p: 4, textAlign: "center",
                  boxShadow: "0 8px 32px rgba(13,71,161,0.12)",
                  borderRadius: 3,
                  border: "1px solid #E3F2FD",
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-10px)", boxShadow: "0 12px 48px rgba(13,71,161,0.2)" },
                }}
              >
                <Box sx={{ color: BLUE, mb: 2 }}>{stat.icon}</Box>
                <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, mb: 1 }}>{stat.number}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{stat.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Story ── */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="overline" sx={{ color: BLUE, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.1em" }}>
              OUR STORY
            </Typography>
            <Typography variant="h2" sx={{ my: 3, fontWeight: 700, color: "#0A1929" }}>
              Building the Future, One Solution at a Time
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: "1.1rem", color: "#37474F" }}>
              Founded in 2025, Quantum IT AI LLC is a technology startup born from a passion for solving real business problems with bold, modern solutions. We help businesses harness next-gen tech to build, launch, and scale rapidly.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: "1.1rem", color: "#37474F" }}>
              Our lean, multi-disciplinary team brings technical vision and relentless execution to every project. We partner closely with startups and enterprises who want to move fast, innovate, and make real impact.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: "1.1rem", color: "#37474F" }}>
              As a new company, our journey is just beginning — but our ambition is global and our results are already making waves.
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* ── Values ── */}
      <Box sx={{ bgcolor: "#F5F9FF", py: 8 }} ref={valuesRef}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography variant="overline" sx={{ color: BLUE, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.1em" }}>OUR VALUES</Typography>
            <Typography variant="h2" sx={{ mt: 2, mb: 2, color: "#0A1929" }}>What Drives Us Every Day</Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: "700px", mx: "auto" }}>
              These core principles guide our decisions, shape our culture, and define how we serve our clients
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
                <Card
                  sx={{
                    width: "100%", maxWidth: 320, height: "100%",
                    p: 4, display: "flex", flexDirection: "column", alignItems: "center",
                    bgcolor: "#fff", borderRadius: 4,
                    boxShadow: "0 2px 12px rgba(13,71,161,0.08)",
                    border: "1px solid #E3F2FD",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-10px)", boxShadow: "0 12px 30px rgba(13,71,161,0.15)", border: "1px solid #BBDEFB" },
                    opacity: valuesInView ? 1 : 0,
                    transform: valuesInView ? "translateY(0)" : "translateY(30px)",
                    transitionDelay: `${index * 0.07}s`,
                  }}
                >
                  <Box sx={{ background: "rgba(13,71,161,0.08)", color: BLUE, width: 70, height: 70, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: "center", color: "#0A1929" }}>{value.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: "center" }}>{value.description}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Culture ── */}
      <Container maxWidth="lg" sx={{ py: 12 }} ref={cultureRef}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography variant="overline" sx={{ color: BLUE, fontWeight: 700, fontSize: "1rem", letterSpacing: "0.1em" }}>COMPANY CULTURE</Typography>
          <Typography variant="h2" sx={{ mt: 2, mb: 2, color: "#0A1929" }}>Where Talent Thrives</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: "700px", mx: "auto" }}>
            We believe in creating an environment where innovation flourishes and people grow
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {culture.map((item, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                sx={{
                  p: 4, borderRadius: 3,
                  boxShadow: "0 2px 12px rgba(13,71,161,0.07)",
                  border: "1px solid #E3F2FD",
                  transition: "all 0.3s ease",
                  opacity: cultureInView ? 1 : 0,
                  transform: cultureInView ? "translateX(0)" : "translateX(-30px)",
                  transitionDelay: `${index * 0.15}s`,
                  "&:hover": { boxShadow: "0 8px 32px rgba(13,71,161,0.14)", border: "1px solid #BBDEFB" },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#0A1929" }}>{item.title}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: DARK }}>{item.percentage}%</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>{item.description}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={cultureInView ? item.percentage : 0}
                  sx={{
                    height: 8, borderRadius: 4,
                    bgcolor: "rgba(13,71,161,0.08)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      background: `linear-gradient(90deg, ${DARK} 0%, ${LIGHT} 100%)`,
                      transition: "transform 1.5s ease",
                    },
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
